import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 2rem;
`;

const Content = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.colors.accent};
  padding-bottom: 0.5rem;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.text};
  margin: 1.5rem 0 1rem 0;
`;

const List = styled.ul`
  margin: 1rem 0;
  padding-left: 2rem;
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const PrivacyPolicyPage = () => {
  return (
    <Container>
      <Title>Privacy Policy</Title>
      <Content>
        <p><strong>Effective Date:</strong> July 22, 2025</p>
        <p><strong>Last Updated:</strong> July 22, 2025</p>

        <Section>
          <SectionTitle>1. Introduction</SectionTitle>
          <p>
            At RPL Support, we are committed to protecting your privacy and ensuring the security of your personal information.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you visit our website
            (https://rplsupport.com.au/) and use our services. We comply with the Australian Privacy Principles (APPs) under
            the Privacy Act 1988 (Cth) and other applicable privacy laws.
          </p>
          <p>
            By accessing or using our website and services, you acknowledge that you have read and understood this Privacy Policy.
            If you do not agree with any part of this policy, please do not use our services.
          </p>
        </Section>

        <Section>
          <SectionTitle>2. Who We Are</SectionTitle>
          <p>
            RPL Support is a leading provider of Recognition of Prior Learning (RPL) assessment services and educational support.
            Our registered business details are:
          </p>
          <List>
            <ListItem><strong>Business Name:</strong> RPL Support Pty Ltd</ListItem>
            <ListItem><strong>Website:</strong> https://rplsupport.com.au/</ListItem>
            <ListItem><strong>Email:</strong> <a href="mailto:privacy@rplsupport.com.au">privacy@rplsupport.com.au</a></ListItem>
            <ListItem><strong>Phone:</strong> 1300 123 456</ListItem>
            <ListItem><strong>Address:</strong> Level 5, 123 Business Street, Melbourne VIC 3000, Australia</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Information We Collect</SectionTitle>

          <SubSectionTitle>3.1 Personal Information</SubSectionTitle>
          <p>We collect personal information that you voluntarily provide to us, including:</p>
          <List>
            <ListItem><strong>Contact Information:</strong> Name, email address, phone number, postal address</ListItem>
            <ListItem><strong>Professional Information:</strong> Employment history, qualifications, skills, certifications</ListItem>
            <ListItem><strong>Educational Background:</strong> Previous education, training records, academic transcripts</ListItem>
            <ListItem><strong>Identification Documents:</strong> Driver's license, passport, birth certificate (for verification purposes)</ListItem>
            <ListItem><strong>Financial Information:</strong> Payment details, billing address (processed securely through third-party providers)</ListItem>
            <ListItem><strong>Communication Preferences:</strong> Marketing preferences, newsletter subscriptions</ListItem>
          </List>

          <SubSectionTitle>3.2 Automatically Collected Information</SubSectionTitle>
          <p>When you visit our website, we automatically collect:</p>
          <List>
            <ListItem>IP address and geolocation data</ListItem>
            <ListItem>Browser type and version</ListItem>
            <ListItem>Device identifiers and operating system</ListItem>
            <ListItem>Referring website addresses</ListItem>
            <ListItem>Pages visited and time spent on each page</ListItem>
            <ListItem>Clickstream data and navigation patterns</ListItem>
            <ListItem>Date and time of visits</ListItem>
          </List>

          <SubSectionTitle>3.3 Sensitive Information</SubSectionTitle>
          <p>
            In the course of providing RPL services, we may collect sensitive information such as:
          </p>
          <List>
            <ListItem>Professional memberships and associations</ListItem>
            <ListItem>Workplace performance records</ListItem>
            <ListItem>Training and competency assessments</ListItem>
          </List>
          <p>
            We only collect sensitive information with your explicit consent and where it is reasonably necessary
            for our business functions or activities.
          </p>
        </Section>

        <Section>
          <SectionTitle>4. How We Collect Information</SectionTitle>
          <List>
            <ListItem><strong>Direct Collection:</strong> When you fill out forms, register for services, contact us, or use our online platforms</ListItem>
            <ListItem><strong>Automated Collection:</strong> Through cookies, web beacons, and similar tracking technologies</ListItem>
            <ListItem><strong>Third Parties:</strong> From educational institutions, employers, training organizations, and government agencies (with your consent)</ListItem>
            <ListItem><strong>Public Sources:</strong> From publicly available professional profiles and registries</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>5. Purpose of Collection</SectionTitle>
          <p>We collect and use your personal information for the following purposes:</p>
          <List>
            <ListItem><strong>Service Delivery:</strong> To provide RPL assessment services and educational support</ListItem>
            <ListItem><strong>Verification:</strong> To verify your identity, qualifications, and work experience</ListItem>
            <ListItem><strong>Communication:</strong> To respond to your inquiries and provide customer support</ListItem>
            <ListItem><strong>Quality Assurance:</strong> To maintain and improve the quality of our services</ListItem>
            <ListItem><strong>Compliance:</strong> To meet legal and regulatory obligations</ListItem>
            <ListItem><strong>Marketing:</strong> To send you relevant information about our services (with your consent)</ListItem>
            <ListItem><strong>Analytics:</strong> To understand how our website is used and improve user experience</ListItem>
            <ListItem><strong>Security:</strong> To protect against fraud, unauthorized access, and other security issues</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Cookies and Tracking Technologies</SectionTitle>

          <SubSectionTitle>6.1 Types of Cookies We Use</SubSectionTitle>
          <List>
            <ListItem><strong>Essential Cookies:</strong> Required for basic website functionality and security</ListItem>
            <ListItem><strong>Performance Cookies:</strong> Collect anonymous statistics about website usage</ListItem>
            <ListItem><strong>Functional Cookies:</strong> Remember your preferences and settings</ListItem>
            <ListItem><strong>Marketing Cookies:</strong> Track visitors across websites to display relevant advertisements</ListItem>
          </List>

          <SubSectionTitle>6.2 Cookie Duration</SubSectionTitle>
          <List>
            <ListItem><strong>Session Cookies:</strong> Deleted when you close your browser</ListItem>
            <ListItem><strong>Persistent Cookies:</strong> Remain on your device for a set period (up to 2 years)</ListItem>
            <ListItem><strong>Third-party Cookies:</strong> Set by external services we use (analytics, social media)</ListItem>
          </List>

          <SubSectionTitle>6.3 Managing Cookies</SubSectionTitle>
          <p>
            You can control and manage cookies through your browser settings. However, disabling certain cookies
            may affect the functionality of our website. We provide a cookie consent banner when you first visit
            our site, allowing you to choose which types of cookies to accept.
          </p>
        </Section>

        <Section>
          <SectionTitle>7. Comments and User-Generated Content</SectionTitle>
          <p>
            When visitors leave comments, reviews, or other user-generated content on our site, we collect the data
            shown in the submission form, along with the visitor's IP address and browser user agent string to help
            with spam detection and security.
          </p>
          <p>
            An anonymized string created from your email address (hash) may be provided to the Gravatar service
            to check if you're using it. The Gravatar service privacy policy is available at:
            <a href="https://automattic.com/privacy/" target="_blank" rel="noopener noreferrer">https://automattic.com/privacy/</a>
          </p>
          <p>
            Once your comment is approved, your profile picture (if using Gravatar) becomes visible to the public
            in the context of your comment. We moderate all user-generated content to ensure compliance with our
            community guidelines.
          </p>
        </Section>

        <Section>
          <SectionTitle>8. Media Uploads</SectionTitle>
          <p>
            If you upload images or documents to our website (such as evidence for RPL assessments), you should
            avoid uploading files with embedded location data (EXIF GPS) included. Visitors to the website can
            download and extract any location data from images on the website.
          </p>
          <p>
            We recommend reviewing and removing any sensitive metadata from files before uploading. We implement
            security measures to protect uploaded documents, but we cannot guarantee complete protection against
            unauthorized access.
          </p>
        </Section>

        <Section>
          <SectionTitle>9. Embedded Content from Other Websites</SectionTitle>
          <p>
            Articles and pages on our site may include embedded content (e.g., videos, images, articles, etc.)
            from other websites. Embedded content from other websites behaves in the exact same way as if the
            visitor has visited the other website.
          </p>
          <p>
            These websites may collect data about you, use cookies, embed additional third-party tracking, and
            monitor your interaction with that embedded content, including tracking your interaction with the
            embedded content if you have an account and are logged in to that website.
          </p>
          <p>
            We are not responsible for the privacy practices of these third-party websites. We recommend reviewing
            their privacy policies separately.
          </p>
        </Section>

        <Section>
          <SectionTitle>10. Data Sharing and Disclosure</SectionTitle>

          <SubSectionTitle>10.1 Third-Party Service Providers</SubSectionTitle>
          <p>We may share your information with trusted third-party service providers who assist us in operating our business, including:</p>
          <List>
            <ListItem>Cloud storage and hosting providers</ListItem>
            <ListItem>Payment processors</ListItem>
            <ListItem>Email marketing platforms</ListItem>
            <ListItem>Customer support software</ListItem>
            <ListItem>Analytics and tracking services</ListItem>
            <ListItem>Assessment and verification partners</ListItem>
          </List>
          <p>All third-party providers are required to maintain the confidentiality and security of your information.</p>

          <SubSectionTitle>10.2 Legal Requirements</SubSectionTitle>
          <p>We may disclose your information when required by law or to comply with legal processes, including:</p>
          <List>
            <ListItem>Court orders and subpoenas</ListItem>
            <ListItem>Government agency requests</ListItem>
            <ListItem>Regulatory compliance requirements</ListItem>
            <ListItem>Law enforcement investigations</ListItem>
          </List>

          <SubSectionTitle>10.3 Business Transfers</SubSectionTitle>
          <p>
            In the event of a merger, acquisition, or sale of all or a portion of our assets, your information
            may be transferred as part of that transaction. We will notify you via email and/or prominent notice
            on our website of any change in ownership or uses of your personal information.
          </p>
        </Section>

        <Section>
          <SectionTitle>11. International Data Transfers</SectionTitle>
          <p>
            We primarily store and process your data within Australia. However, some of our service providers
            may be located overseas. When we transfer your information internationally, we ensure appropriate
            safeguards are in place, including:
          </p>
          <List>
            <ListItem>Standard contractual clauses approved by the Australian government</ListItem>
            <ListItem>Binding corporate rules for intra-group transfers</ListItem>
            <ListItem>Ensuring the recipient country has adequate privacy protections</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>12. Data Retention</SectionTitle>
          <p>We retain your personal information for the following periods:</p>
          <List>
            <ListItem><strong>Assessment Records:</strong> 7 years from the date of completion (as required by vocational education standards)</ListItem>
            <ListItem><strong>Financial Records:</strong> 7 years (as required by Australian tax law)</ListItem>
            <ListItem><strong>Marketing Communications:</strong> Until you unsubscribe or 3 years after last interaction</ListItem>
            <ListItem><strong>Website Analytics:</strong> 26 months</ListItem>
            <ListItem><strong>Support Inquiries:</strong> 2 years from the date of resolution</ListItem>
          </List>
          <p>
            After these periods, we will securely delete or anonymize your information, unless we are legally
            required to retain it for a longer period.
          </p>
        </Section>

        <Section>
          <SectionTitle>13. Your Rights and Choices</SectionTitle>

          <SubSectionTitle>13.1 Access Rights</SubSectionTitle>
          <p>
            You have the right to request access to the personal information we hold about you. You can request
            an exported file of your personal data by contacting us at privacy@rplsupport.com.au.
          </p>

          <SubSectionTitle>13.2 Correction Rights</SubSectionTitle>
          <p>
            You can request correction of any inaccurate or incomplete personal information we hold about you.
            Registered users can update their information through their account dashboard.
          </p>

          <SubSectionTitle>13.3 Deletion Rights</SubSectionTitle>
          <p>
            You can request that we delete your personal information, subject to certain exceptions (such as
            legal obligations or ongoing assessments). This does not include data we are obliged to keep for
            administrative, legal, or security purposes.
          </p>

          <SubSectionTitle>13.4 Opt-Out Rights</SubSectionTitle>
          <p>
            You can opt out of marketing communications at any time by clicking the "unsubscribe" link in
            our emails or by contacting us directly.
          </p>

          <SubSectionTitle>13.5 Data Portability</SubSectionTitle>
          <p>
            You have the right to receive your personal information in a structured, commonly used, and
            machine-readable format, and to transmit this data to another organization.
          </p>
        </Section>

        <Section>
          <SectionTitle>14. Security Measures</SectionTitle>
          <p>We implement appropriate technical and organizational security measures to protect your information:</p>
          <List>
            <ListItem><strong>Encryption:</strong> All data is encrypted in transit using TLS 1.3 and at rest using AES-256</ListItem>
            <ListItem><strong>Access Controls:</strong> Role-based access controls and multi-factor authentication</ListItem>
            <ListItem><strong>Regular Audits:</strong> Security assessments and penetration testing</ListItem>
            <ListItem><strong>Staff Training:</strong> Regular privacy and security training for all employees</ListItem>
            <ListItem><strong>Incident Response:</strong> Comprehensive breach notification procedures</ListItem>
            <ListItem><strong>Secure Infrastructure:</strong> ISO 27001 certified hosting providers</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>15. Children's Privacy</SectionTitle>
          <p>
            Our services are not directed to children under 16 years of age. We do not knowingly collect personal
            information from children. If you believe we have inadvertently collected information from a child,
            please contact us immediately so we can take appropriate action.
          </p>
        </Section>

        <Section>
          <SectionTitle>16. Third-Party Links</SectionTitle>
          <p>
            Our website may contain links to third-party websites, applications, and services. We are not responsible
            for the privacy practices or content of these third-party sites. We encourage you to review the privacy
            policies of any third-party sites you visit.
          </p>
        </Section>

        <Section>
          <SectionTitle>17. Changes to This Privacy Policy</SectionTitle>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the
            new Privacy Policy on this page and updating the "Last Updated" date at the top. In the case of
            significant changes, we will provide additional notice through email or prominent notices on our website.
          </p>
          <p>
            Your continued use of our services after any changes to this Privacy Policy constitutes your acceptance
            of those changes.
          </p>
        </Section>

        <Section>
          <SectionTitle>18. Contact Information</SectionTitle>
          <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
          <List>
            <ListItem><strong>Privacy Officer:</strong> RPL Support Privacy Team</ListItem>
            <ListItem><strong>Email:</strong> <a href="mailto:privacy@rplsupport.com.au">privacy@rplsupport.com.au</a></ListItem>
            <ListItem><strong>Phone:</strong> 1300 123 456 (Monday-Friday, 9 AM - 5 PM AEST)</ListItem>
            <ListItem><strong>Mail:</strong> Privacy Officer, RPL Support Pty Ltd, Level 5, 123 Business Street, Melbourne VIC 3000</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>19. Making a Privacy Complaint</SectionTitle>
          <p>
            If you believe we have breached your privacy rights, please contact us immediately. We take all complaints
            seriously and will respond within 30 days. If you are not satisfied with our response, you can contact the
            Office of the Australian Information Commissioner (OAIC):
          </p>
          <List>
            <ListItem><strong>Website:</strong> <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">https://www.oaic.gov.au</a></ListItem>
            <ListItem><strong>Phone:</strong> 1300 363 992</ListItem>
            <ListItem><strong>Email:</strong> enquiries@oaic.gov.au</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>20. Additional Information for Specific Regions</SectionTitle>

          <SubSectionTitle>20.1 Australian Privacy Rights</SubSectionTitle>
          <p>
            Under the Privacy Act 1988 (Cth), Australian residents have specific rights regarding their personal information.
            These include the right to access and correct personal information, and to complain about privacy breaches.
          </p>

          <SubSectionTitle>20.2 European Union (GDPR) Rights</SubSectionTitle>
          <p>
            If you are located in the European Economic Area, you have additional rights under the General Data Protection
            Regulation (GDPR), including the right to data portability and the right to object to processing.
          </p>
        </Section>

        <p style={{ marginTop: '3rem', fontStyle: 'italic' }}>
          This Privacy Policy was last updated on July 22, 2025. Please check back regularly for updates.
        </p>
      </Content>
    </Container>
  );
};

export default PrivacyPolicyPage; 